/*!

=========================================================
* Argon Dashboard React - v1.2.3
=========================================================

* Product Page: https://www.creative-tim.com/product/argon-dashboard-react
* Copyright 2023 Creative Tim (https://www.creative-tim.com)
* Licensed under MIT (https://github.com/creativetimofficial/argon-dashboard-react/blob/master/LICENSE.md)

* Coded by Creative Tim

=========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.

TODO
- Make the demo account have multiple templates (Specificly one with ICD billing codes)
    - Daily Note
    - Evaluation
    - Diagnosis
    - SOAP Note
    - Coding
- Make all users get these when they sign up


- Remove PT from the App - I think it's just the logo
- Update website to say this
- Handle Errors with a status message (Such as, that name is already taken...)
- Make sure we don't get things like
    Documentation by: [Medical Scribe's Name]
    Date and time: [Current Date and Time]
- Favorite Notes
- On hover say somethign or on hover comprehend
- Share (The more people that use it the better it gets) Network Effects and Flywheel

*/

import {
  Card,
  CardHeader,
  Container,
  Row,
  Button,
  Dropdown,
  DropdownToggle,
  DropdownMenu,
  DropdownItem,
} from 'reactstrap';
import { GlobalContext } from '../api/GlobalContext';
import { useContext, useState, useEffect, useRef } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';
import ConfirmationModal from '../components/Misc/ConfirmationModal.js';
import InstructionAssistantModal from 'components/InstructionAssistant/InstructionAssistantModal';
import InstructionUpdateStatusStrip from 'components/InstructionAssistant/InstructionUpdateStatusStrip';
import InstructionUpdateUndoModal from 'components/InstructionAssistant/InstructionUpdateUndoModal';
import { useInstructionAssistantUpdate } from 'components/InstructionAssistant/useInstructionAssistantUpdate';
import ShareTemplateModal from 'components/Misc/ShareTemplateModal';
import Collapsible from '../components/Misc/Collapsible';
import EditableLabel from '../components/Misc/EditableLabel';
import {
  convertAccessLevelForFrontend,
  getEmrNoteTypePayload,
  getEmrNoteTypeValues,
  truncateText,
} from '../components/Misc/utils';
import { fetchUserDetails, fetchInjector } from '../components/Misc/apiUtils';
import {
  onAddSample,
  onEditSample,
  onRemoveSample,
} from '../components/Misc/samplesUtil.js';

import Modal from '../components/Modals/Modal.js';
import RelearnStyleButton from '../components/RelearnStyle/RelearnStyleButton';

// core components
import Header from 'components/Headers/Header';
import HashState from '../variables/hash';
import LoadingBanner from '../components/Misc/LoadingBanner.js';
import NavigationPath from '../components/Misc/NavigationPath';
import { withSavedSubsectionNames } from '../utils/subsectionNames';

const getTemplateNoteTypes = (template) =>
  getEmrNoteTypeValues(template?.emr_note_type);

/**
 * Strips the disabled marker so enabled and disabled copies compare by URL.
 */
const cleanEmrUrl = (url) => String(url || '').replace(/^Disabled - /, '');

/**
 * Matches equivalent EMR URLs even when one side stores a shortened form.
 */
const emrUrlsMatch = (urlA, urlB) => {
  const cleanA = cleanEmrUrl(urlA);
  const cleanB = cleanEmrUrl(urlB);
  return (
    !!cleanA && !!cleanB && (cleanA.includes(cleanB) || cleanB.includes(cleanA))
  );
};

const checkSvg =
  "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 12 12'><path fill='none' stroke='white' stroke-width='2' stroke-linecap='round' stroke-linejoin='round' d='M2.5 6.5l2.5 2.5 4.5-5.5'/></svg>\")";

const checkboxStyle = (checked) => ({
  appearance: 'none',
  WebkitAppearance: 'none',
  MozAppearance: 'none',
  boxSizing: 'border-box',
  width: 16,
  height: 16,
  margin: 0,
  padding: 0,
  borderRadius: 4,
  border: checked ? '1.5px solid var(--primary)' : '1.5px solid #d1d5db',
  background: checked ? 'var(--primary)' : '#fff',
  backgroundImage: checked ? checkSvg : 'none',
  backgroundRepeat: 'no-repeat',
  backgroundPosition: 'center',
  backgroundSize: '12px 12px',
  backgroundOrigin: 'content-box',
  cursor: 'pointer',
  transition: 'background-color 120ms ease, border-color 120ms ease',
  flexShrink: 0,
  display: 'inline-block',
  verticalAlign: 'middle',
});

// extract domain
const getEmrDisplayUrl = (emrUrl) =>
  emrUrl
    ? emrUrl
        .replace(/(https?:\/\/)?(www\.)?(.+\..+)(\/)?(.com)?/i, '$3')
        .replace('.com', '')
    : null;

const MatchNoteSettingModal = ({
  isOpen,
  onClose,
  name,
  emrUrl,
  availableNoteTypes,
  selectedNoteTypes,
  onToggleNoteType,
  onSave,
}) => {
  const saveButtonText = `Save (${selectedNoteTypes.length} selected)`;
  const emrDisplayUrl = getEmrDisplayUrl(emrUrl);

  return (
    <Modal isOpen={isOpen} onClose={onClose} isCloseButton={false}>
      <div
        style={{
          width: '100%',
          boxSizing: 'border-box',
          display: 'flex',
          flexDirection: 'column',
          minHeight: 0,
          flex: '1 1 auto',
        }}
      >
        <div style={{ padding: '20px 20px 12px' }}>
          <h2
            style={{
              margin: 0,
              fontSize: '1.25rem',
              fontWeight: 600,
              textAlign: 'left',
            }}
          >
            Apply Settings to Other Note Types
          </h2>
          <p
            style={{
              margin: '6px 0 0',
              color: '#6c757d',
              fontSize: '0.8rem',
              lineHeight: 1.45,
              textAlign: 'left',
            }}
          >
            Selected note types will use the same AI settings as:
          </p>
          <div
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 6,
              maxWidth: '100%',
              marginTop: 8,
              padding: '4px 9px',
              borderRadius: 999,
              background: '#f7faf8',
              border: '1px solid #dbe7df',
              color: '#1f2937',
              fontSize: '0.78rem',
              fontWeight: 600,
              lineHeight: 1.25,
            }}
          >
            <span
              aria-hidden="true"
              style={{
                width: 6,
                height: 6,
                borderRadius: '50%',
                background: 'var(--primary)',
                flexShrink: 0,
              }}
            />
            <span
              style={{
                minWidth: 0,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {name}
            </span>
          </div>
        </div>

        <div
          style={{
            background: '#fff',
            display: 'flex',
            flexDirection: 'column',
            minHeight: 0,
            flex: '1 1 auto',
          }}
        >
          <div
            style={{
              overflowY: 'auto',
              flex: '1 1 auto',
              minHeight: 0,
              padding: '4px 0',
            }}
          >
            {availableNoteTypes.length === 0 ? (
              <p
                style={{
                  margin: 0,
                  padding: '16px',
                  color: '#6c757d',
                  fontSize: '0.875rem',
                }}
              >
                No EMR note types found for this EMR.
              </p>
            ) : (
              availableNoteTypes.map((noteTypeValue) => {
                const checked = selectedNoteTypes.includes(noteTypeValue);

                return (
                  <label
                    key={noteTypeValue}
                    style={{
                      display: 'grid',
                      gridTemplateColumns: '16px minmax(0, 1fr)',
                      alignItems: 'flex-start',
                      columnGap: 8,
                      padding: '9px 8px',
                      cursor: 'pointer',
                      margin: 0,
                      borderRadius: 6,
                      background: checked ? '#f1fbf4' : 'transparent',
                      borderLeft: '4px solid transparent',
                      boxShadow: 'none',
                      wordBreak: 'break-word',
                      transition:
                        'background-color 120ms ease, box-shadow 120ms ease',
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={checked}
                      onChange={() => onToggleNoteType(noteTypeValue)}
                      style={{
                        ...checkboxStyle(checked),
                        marginTop: 3,
                      }}
                    />
                    <span
                      style={{
                        display: 'flex',
                        flexDirection: 'column',
                        minWidth: 0,
                        alignItems: 'flex-start',
                      }}
                    >
                      <span
                        style={{
                          fontSize: '0.875rem',
                          color: '#111827',
                          lineHeight: 1.25,
                          textAlign: 'left',
                        }}
                      >
                        {noteTypeValue}
                      </span>
                      {emrDisplayUrl ? (
                        <span
                          style={{
                            display: 'block',
                            fontSize: '0.72rem',
                            color: '#9ca3af',
                            lineHeight: 1.25,
                            marginTop: 3,
                            textAlign: 'left',
                          }}
                        >
                          {emrDisplayUrl}
                        </span>
                      ) : null}
                    </span>
                  </label>
                );
              })
            )}
          </div>
        </div>

        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 12,
            flexWrap: 'wrap',
            padding: '12px 20px 16px',
            flexShrink: 0,
          }}
        >
          <span
            style={{
              fontSize: '0.8rem',
              color: selectedNoteTypes.length === 0 ? '#dc3545' : 'transparent',
              minHeight: '1em',
              flex: '1 1 auto',
            }}
          >
            Select at least one note type.
          </span>
          <div style={{ display: 'flex', gap: 8, flexShrink: 0 }}>
            <Button color="secondary" onClick={onClose}>
              Cancel
            </Button>
            <Button
              color="primary"
              onClick={onSave}
              disabled={selectedNoteTypes.length === 0}
            >
              {saveButtonText}
            </Button>
          </div>
        </div>
      </div>
    </Modal>
  );
};

const TemplateDetails = () => {
  const navigate = useNavigate();
  const location = useLocation();

  // Default Values for the fields
  const defaultName = 'Template Name';
  const defaultQuestions = {};
  const defaultSamples = {};
  const defaultTemplateReply = {};
  // Get our Globals
  const { apiClient } = useContext(GlobalContext);

  // State Variables
  const [aiThinking, setAiThinking] = useState(false);
  const [configID, setConfigID] = useState(-1); // This is the ID of the conversation we are looking at
  const [refresh, _setRefresh] = useState(false); // This is used to force a refresh of the conversation fields (like when we change the config)
  const [name, _setName] = useState(defaultName);
  const [questions, _setQuestions] = useState(defaultQuestions);
  const [samples, _setSamples] = useState(defaultSamples);
  const [viewMode, setViewMode] = useState('');
  const [isShareModalOpen, setIsShareModalOpen] = useState(false);
  const [sharedAccessData, setSharedAccessData] = useState([]);
  const [userEmail, setUserEmail] = useState(''); // State to hold user's email address
  const [showEnabled, setShowEnabled] = useState(false); // State to hold the enabled status of the template
  const [enabled, setEnabled] = useState(true); // State to hold the enabled status of the template
  const [trainingData, setTrainingData] = useState({});
  const [haveInjector, setHaveInjector] = useState();
  const [confirmationDetails, setConfirmationDetails] = useState({
    isOpen: false,
    type: '', // 'sample', 'template', or 'promote'
    questionKey: '',
    index: -1,
  });
  const [modalOpen, setModalOpen] = useState(false);
  const [modalText, setModalText] = useState('');
  const [modalButtons, setModalButtons] = useState('');
  const [hasBuilderDocs, setHasBuilderDocs] = useState(false);
  const hash = HashState();
  const [showEditor, setShowEditor] = useState(false);
  const [noteType, setNoteType] = useState(null);
  const [emrUrl, setEmrUrl] = useState(null);
  const [noteTypes, setNoteTypes] = useState([]);
  const [isMatchNoteSettingModalOpen, setIsMatchNoteSettingModalOpen] =
    useState(false);
  const [availableNoteTypes, setAvailableNoteTypes] = useState([]);
  const [selectedNoteTypes, setSelectedNoteTypes] = useState([]);
  const [isDev, setIsDev] = useState(false);
  const [isPromoted, setIsPromoted] = useState(false);
  const [templateReply, setTemplateReply] = useState(defaultTemplateReply);
  const [promoteSuccessModal, setPromoteSuccessModal] = useState({
    isOpen: false,
    configId: null,
    emr: null,
  });
  const [showViewOnlyModal, setShowViewOnlyModal] = useState(false);
  const [expandedSections, setExpandedSections] = useState(false);
  const [subsections, setSubsections] = useState({});
  const [instructionAssistantOpen, setInstructionAssistantOpen] =
    useState(false);
  // Instruction Assistant apply + undo lifecycle (template-only: no EMR scrape, so
  // no section metadata and no note path).
  const instructionAssistantUpdate = useInstructionAssistantUpdate({
    apiClient,
    configId: configID,
    instructions: {
      getCurrent: () => questions,
      applyLocal: (revisedInstructions) =>
        _setQuestions((currentQuestions) => ({
          ...currentQuestions,
          ...revisedInstructions,
        })),
    },
    examples: {
      getCurrent: () => samples,
      applyLocal: (revisedExamples) =>
        _setSamples((currentSamples) => ({
          ...currentSamples,
          ...revisedExamples,
        })),
    },
  });

  const handleViewOnlyClick = (e) => {
    if (e) e.stopPropagation();
    setShowViewOnlyModal(true);
  };

  useEffect(() => {
    fetchInjector(setHaveInjector);
  }, []);

  useEffect(() => {
    // Fetch user details
    fetchUserDetails(apiClient)
      .then((userDetails) => {
        // Extract user email from user details
        setUserEmail(userDetails.email || '');
        setHasBuilderDocs(userDetails.hasBuilderDocs);
      })
      .catch((error) => {
        console.error('Error fetching user details:', error);
      });
  }, [apiClient]);

  const setRefresh = () => _setRefresh(!refresh);

  const isMountedRef = useRef(true);
  const inputRef = useRef();
  useEffect(() => {
    // Set isMountedRef to true when the component mounts
    isMountedRef.current = true;

    // Set isMountedRef to false when the component unmounts
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  // Functions to update the state variables (and send to the server)
  const setName = async (newName) => {
    try {
      _setName(newName);
      setAiThinking(true);
      let configDetails = await apiClient.editConfig(
        configID,
        'name',
        '',
        newName,
      );
      setAiThinking(false);
      //showing the alert only when user is still on the page
      if (
        'error' in configDetails &&
        isMountedRef.current &&
        configDetails.error === 'New config name already exists'
      ) {
        alert('Template with same name exists. Please try a different name.');
        return;
      }
    } catch (error) {
      console.error('Error editing config:', error);
    }
  };

  const openConfirmationModal = (type, questionKey, index) => {
    setConfirmationDetails({ isOpen: true, type, questionKey, index });
  };

  const handleConfirmAction = () => {
    if (confirmationDetails.type === 'sample') {
      onRemoveSample(
        confirmationDetails.questionKey,
        confirmationDetails.index,
        configID,
        _setSamples,
        apiClient,
      );
    } else if (confirmationDetails.type === 'template') {
      updateQuestion(confirmationDetails.questionKey, 'remove');
    } else if (confirmationDetails.type === 'promote') {
      apiClient
        .devPromote(configID)
        .then((response) => {
          if (response && response.configId && response.emr) {
            // Success - show success modal
            setPromoteSuccessModal({
              isOpen: true,
              configId: response.configId,
              emr: response.emr,
            });
          } else {
            console.error('Unexpected response from promote:', response);
          }
        })
        .catch((error) => {
          console.error('Error promoting template:', error);
        });
    }
    setConfirmationDetails({ ...confirmationDetails, isOpen: false });
  };

  const setQuestions = (newQuestions, action, key, value, newKey) => {
    // Make the API call to update the configuration on the backend
    setAiThinking(true);
    apiClient
      .editConfig(configID, action, key, value, newKey)
      .then((data) => {
        setAiThinking(false);
        if ('error' in data) {
          console.log('Error editing config:', data.error);
        } else {
          console.log('Config successfully updated');
        }
      })
      .catch((error) => {
        console.log(`Unexpected error from comprehend - ${error}`);
      });
    _setQuestions(newQuestions);
  };

  useEffect(() => {
    // Reset the state variables
    _setName(defaultName);
    _setQuestions(defaultQuestions);
    _setSamples(defaultSamples);
    setTemplateReply(defaultTemplateReply);
    setExpandedSections(false);
    setSubsections({});
    if (hash === '') {
      console.log('No conversation id specified');
      navigate('/');
      return;
    }
    setConfigID(parseInt(hash, 10));
  }, [hash]);

  useEffect(() => {
    // Go get the conversation details for this conversation
    // const hash = window.location.hash.substr(1);
    // Get the conversation details
    if (configID != -1) {
      apiClient
        .getConfigDetails(configID)
        .then((data) => {
          // console.log(data);
          if ('error' in data) {
            console.log('Error getting config details' + configID);
            return;
          }
          // only set the values if the field is not null or undefined
          _setName(data.name || defaultName);
          _setQuestions(data.questions || defaultQuestions);
          _setSamples(data.samples || defaultSamples);
          setShowEnabled(
            data.emr_url && data.owner == apiClient.email ? true : false,
          );
          setEnabled(data.emr_url?.startsWith('Disabled') ? false : true);
          // This helps to set the view based on access level
          setViewMode(convertAccessLevelForFrontend(data.access_level));
          // This helps to show how many people have access to the template
          const sharedAccessData = data.sharedAccess || {};
          const sharedData = Object.entries(sharedAccessData).map(
            ([email, access]) => ({
              email,
              access: convertAccessLevelForFrontend(access),
            }),
          );
          setSharedAccessData(sharedData);
          setTrainingData(data.training_data || {});
          setEmrUrl(data.emr_url || null);
          const configNoteTypes = getTemplateNoteTypes(data);
          setNoteType(configNoteTypes.length > 0 ? configNoteTypes[0] : null);
          setNoteTypes(configNoteTypes);
          // Set isDev from apiClient
          setIsDev(apiClient.isDev || false);
          // Check if template is already promoted
          setIsPromoted(!!data.config_library_id);
          setTemplateReply(data.replyType || defaultTemplateReply);
          const extractedSubsections = {};
          const qs = data.questions || {};
          Object.keys(qs).forEach((k) => {
            const val = qs[k];
            try {
              const parsed = typeof val === 'string' ? JSON.parse(val) : val;
              if (parsed?.SubSections && typeof parsed.SubSections === 'object') {
                extractedSubsections[k] = parsed.SubSections;
              }
            } catch {
              // not JSON, no subsections
            }
          });
          setSubsections(withSavedSubsectionNames(extractedSubsections, data.subsectionNames));
          setExpandedSections(true);
        })
        .catch((error) => {
          // probably no session key or no ConvoID Yet
          console.log(error);
          console.log('Did not get config details');
        });
    }
  }, [apiClient, configID, refresh]);

  useEffect(() => {
    if (inputRef.current) {
      inputRef.current.focus();
    }
  }, [showEditor]);

  const updateQuestion = (key, newAnswer) => {
    //remove the question or update the value
    // Trim spaces and newlines, replace multiple spaces with single space
    const trimmedKey = key.replace(/\s+/g, ' ').trim();
    if (!trimmedKey) return; // Don't update if empty after trimming
    const newQuestions = { ...questions };
    let action = 'update_question_value';
    if (newAnswer === 'remove') {
      delete newQuestions[trimmedKey];
      action = 'remove_question';
    } else {
      newQuestions[trimmedKey] = newAnswer;
    }
    setQuestions(newQuestions, action, trimmedKey, newAnswer);
  };

  const setTemplateReplyHandler = async (questionKey, newValue) => {
    try {
      // Update local state immediately for responsiveness
      setTemplateReply((prev) => {
        const updated = { ...(prev || {}) };
        updated[questionKey] = newValue;
        return updated;
      });

      // Persist replyType for this section to the backend
      setAiThinking(true);
      await apiClient.editConfig(configID, 'replyType', questionKey, newValue);
    } catch (error) {
      console.error('Error updating reply type:', error);
    } finally {
      setAiThinking(false);
    }
  };

  const newKey = (newKey, oldKey) => {
    // Update Question Key
    // var bulkEdit = [];
    const newQuestions = updateKey(questions, oldKey, newKey);
    // Call setQuestions with 'update_question' action
    const bulkEdit = [
      {
        fieldName: 'update_question',
        key: oldKey,
        value: '',
        keyNew: newKey.replace(/\s+/g, ' ').trim(),
      },
    ]; // update the key of the question

    // Update the samples associated with the old key to the new key
    if (samples[oldKey]) {
      bulkEdit.push(
        {
          // Update the samples to the new Key
          fieldName: 'samples',
          key: newKey,
          value: samples[oldKey],
        },
        {
          // Delete samples under the old key
          fieldName: 'samples',
          key: oldKey,
          value: [],
        },
      );
    }

    if (bulkEdit.length > 0) {
      setAiThinking(true);
      apiClient
        .editConfigBulk(configID, bulkEdit)
        .then((data) => {
          setAiThinking(false);
          if ('error' in data) {
            console.error(`Error editing template - ${data.error}`);
            return;
          }
        })
        .catch((error) => {
          console.error(`Unexpected Error from template edit - ${error}`);
        });
    }
    _setQuestions(newQuestions); // Reset the questions with the new key

    // Call the function to update sample key if needed
    updateSamples(oldKey, newKey, _setSamples);
  };

  const updateSamples = (oldKey, newKey, _setSamples) => {
    _setSamples((prevSamples) => {
      const updatedSamples = { ...prevSamples };

      // Check if oldKey exists
      if (oldKey in updatedSamples) {
        const oldQuestionSamples = updatedSamples[oldKey];
        // Update the samples with the new key
        updatedSamples[newKey] = oldQuestionSamples;
      }
      return updatedSamples;
    });
  };

  const updateKey = (obj, oldKey, newKey) => {
    const entries = Object.entries(obj);
    const newEntries = entries.map(([key, value]) =>
      key === oldKey ? [newKey, value] : [key, value],
    );
    return Object.fromEntries(newEntries);
  };

  function addSection() {
    // Add a new section to the template
    // Convert questions object to array of objects so we can add a new section on top with unshift
    const questionsArray = Object.entries(questions).map(([key, value]) => ({
      key,
      value,
    }));

    // Create the new section object
    const newSection = { key: 'New Section', value: '' };

    // Add the new section at the top of the array
    questionsArray.unshift(newSection);

    // Convert the array back to an object
    const newQuestions = questionsArray.reduce((obj, item) => {
      obj[item.key] = item.value;
      return obj;
    }, {});
    setQuestions(
      newQuestions,
      'add_question',
      newSection.key,
      newSection.value,
    );
  }

  function reorderObject(originalObject, key, newIndex) {
    // Check if the key exists in the original object
    if (!originalObject.hasOwnProperty(key)) {
      throw new Error('Key does not exist in the object.');
    }

    // Check if the newIndex is within the valid range
    const keys = Object.keys(originalObject);
    if (newIndex < 0 || newIndex >= keys.length) {
      throw new Error('Invalid new index.');
    }

    // Remove the key from its current position
    const filteredKeys = keys.filter((k) => k !== key);

    // Insert the key at the new index
    filteredKeys.splice(newIndex, 0, key);

    // Build and return the new object
    const newObject = {};
    filteredKeys.forEach((k) => {
      newObject[k] = originalObject[k];
    });

    return newObject;
  }

  const handleDragEnd = (result) => {
    // console.log(result)
    // Check if the item is dropped outside the list
    if (!result.destination) {
      // If so, do nothing and put it back where it was
      return;
    }

    // Check if the location did not change
    if (result.source.index === result.destination.index) {
      // If so, do nothing
      return;
    }

    // Reorder your list and update state
    // console.log(questions)
    _setQuestions(
      reorderObject(questions, result.draggableId, result.destination.index),
    );
    // console.log(questions)

    // Make your API call
    apiClient
      .reorderConfig(configID, result.draggableId, result.destination.index)
      .then((data) => {
        if ('error' in data) {
          console.log('Error reordering config', data);
          return;
        }
      })
      .catch((error) => {
        console.log(`Unexpected Error from comprehend - ${error}`);
      });
  };

  const duplicateTemplate = () => {
    console.log(apiClient.name);
    const existingConfig = configID; // Replace 'existingConfig' with your existing config
    const newConfig = name + ' - ' + apiClient.name.split(' ')[0] + "'s"; // Generate new config
    apiClient
      .duplicateTemplate(existingConfig, newConfig)
      .then((response) => {
        // Handle the response if needed
        if (
          'error' in response &&
          response.error === 'New config name already exists'
        ) {
          alert('Template with same name exists. Please try a different name.');
          return;
        }
        console.log('Template duplicated:', response);
        const configId = response && response.configId ? response.configId : '';
        setViewMode('Full');
        navigate(`/User/templates/manager#${configId}`);
      })
      .catch((error) => {
        // Handle errors if API call fails
        console.error('Error duplicating template:', error);
        throw error; // Propagate the error to the caller
      });
  };
  const [helpModalOpen, setHelpModalOpen] = useState(false);

  const disableEditing = viewMode === 'View Only' || viewMode === 'Duplicate';
  const showInstructionAssistant = !disableEditing && viewMode !== 'AutoCopy';
  const isConfluentUser = apiClient.isConfluentUser;
  const disableMakeCopyForConfluent =
    isConfluentUser && viewMode === 'View Only';

  const enableDisableTemplate = () => {
    setAiThinking(true);
    apiClient
      .editConfig(configID, 'enable', '', !enabled)
      .then((data) => {
        setAiThinking(false);
        if ('error' in data) {
          console.log('Error editing config');
          return;
        }
        setEnabled(!enabled);
        if (enabled) {
          setModalOpen(true);
          setModalText(
            "Template is disabled and will not appear in the new visit template picker. You can enable it again by clicking 'Enable' in the dropdown menu.",
          );
          setModalButtons([
            {
              label: `OK`,
              color: 'primary',
              onClick: () => {},
            },
          ]);
        }
      })
      .catch((error) => {
        console.log(`Unexpected Error from comprehend - ${error}`);
      });
  };
  const handleShareClick = () => {
    setIsShareModalOpen(true); // Open the ShareTemplateModal
  };

  const closeMatchNoteSettingModal = () => {
    setIsMatchNoteSettingModalOpen(false);
  };

  /**
   * Builds the checkbox catalog from visible configs on this EMR URL and
   * preselects the note types already assigned to the current note setting.
   */
  const openMatchNoteSettingModal = async () => {
    const currentNoteTypes = getEmrNoteTypeValues(noteTypes);
    const configs = await apiClient.getAllConfigs();
    const noteTypeMap = new Map();

    currentNoteTypes.forEach((noteTypeValue) => {
      noteTypeMap.set(noteTypeValue, noteTypeValue);
    });

    configs
      .filter((config) => emrUrlsMatch(config.emr_url, emrUrl))
      .forEach((config) => {
        getTemplateNoteTypes(config).forEach((noteTypeValue) => {
          noteTypeMap.set(noteTypeValue, noteTypeValue);
        });
      });

    setAvailableNoteTypes(Array.from(noteTypeMap.values()));
    setSelectedNoteTypes(currentNoteTypes);
    setIsMatchNoteSettingModalOpen(true);
  };

  const toggleSelectedNoteType = (noteTypeValue) => {
    setSelectedNoteTypes((prev) => {
      const exists = prev.includes(noteTypeValue);
      return exists
        ? prev.filter((selected) => selected !== noteTypeValue)
        : [...prev, noteTypeValue];
    });
  };

  const saveMatchedNoteTypes = async () => {
    if (selectedNoteTypes.length === 0) {
      alert('Select at least one EMR note type.');
      return;
    }

    const response = await apiClient.editConfig(
      configID,
      'emr_note_type',
      '',
      getEmrNoteTypePayload(selectedNoteTypes),
    );
    if (response?.error) {
      alert(
        Array.isArray(response.error)
          ? response.error.join(', ')
          : response.error,
      );
      return;
    }

    setNoteTypes(selectedNoteTypes);
    setNoteType(
      selectedNoteTypes.includes(noteType)
        ? noteType
        : selectedNoteTypes[0],
    );
    setIsMatchNoteSettingModalOpen(false);
  };

  const ActionsDropdown = ({
    duplicateTemplate,
    enableDisableTemplate,
    enabled,
    handleShareClick,
    openMatchNoteSettingModal,
    disableEditing,
    disableMakeCopy,
  }) => {
    const [dropdownOpen, setDropdownOpen] = useState(false);
    const toggleDropdown = () => setDropdownOpen(!dropdownOpen);

    const iconStyle = { color: '#6bd888' }; // Define the icon color

    return (
      <Dropdown isOpen={dropdownOpen} toggle={toggleDropdown}>
        <DropdownToggle color="primary" size="sm">
          <i className="fa fa-bars" aria-hidden="true"></i>
        </DropdownToggle>
        <DropdownMenu right>
          {showEnabled && (
            <DropdownItem onClick={enableDisableTemplate}>
              <i
                className={`fa ${enabled ? 'fa-toggle-on' : 'fa-toggle-off'}`}
                style={iconStyle}
              ></i>
              <span className="ml-2">{enabled ? 'Disable' : 'Enable'}</span>
            </DropdownItem>
          )}
          {!disableMakeCopy && (
            <DropdownItem onClick={duplicateTemplate}>
              <i className="fa fa-copy" style={iconStyle} />{' '}
              <span className="ml-2">Make a Copy</span>
            </DropdownItem>
          )}
          <DropdownItem onClick={handleShareClick} disabled={disableEditing}>
            <i className="fa fa-share" style={iconStyle} />{' '}
            <span className="ml-2">Share</span>
          </DropdownItem>
          {showEnabled && !disableEditing && (
            <DropdownItem onClick={openMatchNoteSettingModal}>
              <i className="fa fa-link" style={iconStyle} />{' '}
              <span className="ml-2">Apply to Note Types</span>
            </DropdownItem>
          )}
          <DropdownItem onClick={() => setHelpModalOpen(true)}>
            <i className="fa fa-question-circle" style={iconStyle} />{' '}
            <span className="ml-2">Help</span>
          </DropdownItem>
        </DropdownMenu>
      </Dropdown>
    );
  };
  const handleCloseModal = () => {
    setModalOpen(false);
    setModalButtons([]); // Reset modalButtons to an empty array
  };

  const handleViewInLibrary = () => {
    // Navigate to library details with the promoted template
    const { configId, emr } = promoteSuccessModal;
    setPromoteSuccessModal({ isOpen: false, configId: null, emr: null });
    navigate(`/User/template/library/details?configId=${configId}&emr=${emr}`);
  };

  const handleStayHere = () => {
    setPromoteSuccessModal({ isOpen: false, configId: null, emr: null });
  };

  const handleViewPromotedTemplate = () => {
    apiClient
      .devViewPromotedTemplate(configID)
      .then((response) => {
        if (response && response.configId && response.emr) {
          // Navigate to library details with the promoted template
          navigate(
            `/User/template/library/details?configId=${response.configId}&emr=${response.emr}`,
          );
        } else {
          console.error(
            'Unexpected response from view promoted template:',
            response,
          );
        }
      })
      .catch((error) => {
        console.error('Error viewing promoted template:', error);
      });
  };

  const breadcrumbItems = [
    {
      name: 'Templates',
      path: `/User/templates?tab=${location.state?.tab || 'personal'}`,
    },
    ...(noteType
      ? [
          {
            name: truncateText(noteType),
            path: '',
          },
        ]
      : []),
    {
      name: (
        <span onClick={() => setShowEditor(true)} style={{ cursor: 'pointer' }}>
          <i className="fa fa-file-text" style={{ marginRight: '5px' }}></i>
          {truncateText(name || 'Untitled')}
          <i
            className="fa fa-pen"
            style={{ marginLeft: '8px', marginRight: '10px' }}
          ></i>
        </span>
      ),
      path: '',
    },
  ];

  return (
    <>
      <LoadingBanner showBanner={aiThinking} loadingText="Updating..." />
      {showInstructionAssistant && (
        <InstructionAssistantModal
          isOpen={instructionAssistantOpen}
          onClose={() => setInstructionAssistantOpen(false)}
          apiClient={apiClient}
          configId={configID}
          onSaveToTemplate={instructionAssistantUpdate.applyTemplate}
        />
      )}
      <InstructionUpdateUndoModal
        isOpen={instructionAssistantUpdate.state.showInstructionModal}
        kind={instructionAssistantUpdate.state.kind}
        oldInstructions={instructionAssistantUpdate.state.oldInstructions}
        newInstructions={instructionAssistantUpdate.state.newInstructions}
        oldExamples={instructionAssistantUpdate.state.oldExamples}
        newExamples={instructionAssistantUpdate.state.newExamples}
        onResolveInstructions={instructionAssistantUpdate.resolveUndo}
      />
      <Header isLite={true} />
      <Modal
        isOpen={modalOpen}
        onClose={handleCloseModal}
        buttons={modalButtons}
      >
        <h3>Template Disabled</h3>
        <p>{modalText}</p>
      </Modal>
      <Modal isOpen={helpModalOpen} onClose={() => setHelpModalOpen(false)}>
        <h2>What do you mean by template?</h2>
        <p>
          We want the notes generated by our software to comprehend your style
          and voice. As such, we allow our users to generate their own template
          for what they want a note to look like. If you want to see some good
          examples of templates, check out the demo's Templates{' '}
          <a href="https://app.comprehendpt.com#demo" target="_blank">
            here.
          </a>
        </p>
        {/* <EditableLabel text="" onChange={setConfig} /> */}
        {/* TODO: Allow them to upload a sample note and we will generalize it to a template */}
      </Modal>
      {/* Page content */}
      <Container className="mt--7" fluid>
        {/* Table */}
        <Row>
          <div className="col">
            <Card className="shadow">
              <NavigationPath paths={breadcrumbItems} />
              <CardHeader
                className="border-0"
                style={{ padding: '0.75rem 1.25rem' }}
              >
                <Modal
                  isOpen={!!showEditor}
                  onClose={() => setShowEditor(false)}
                  isCloseButton={false}
                >
                  <h4>{'Edit Template Name'}</h4>
                  <EditableLabel
                    labelText="Name"
                    text={name}
                    onChange={(newVal) => {
                      setName(newVal);
                    }}
                    className="h3"
                    disabled={disableEditing}
                    inputRef={inputRef}
                  />
                </Modal>
              </CardHeader>
              {enabled ? null : (
                <div
                  className="status-message"
                  style={{
                    padding: '10px',
                    backgroundColor: '#f8d7da',
                    color: '#721c24',
                    fontSize: '0.8rem',
                    marginBottom: '10px',
                  }}
                >
                  <strong>Template is Disabled:</strong> It will not appear in
                  the new visit template picker. You can re-enable it by
                  clicking the 'Enable' button.
                </div>
              )}
              <div className="list-container">
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'flex-start',
                    flexWrap: 'wrap',
                    gap: '10px',
                    paddingInline: '20px',
                  }}
                >
                  <ActionsDropdown
                    duplicateTemplate={duplicateTemplate}
                    enableDisableTemplate={enableDisableTemplate}
                    enabled={enabled}
                    handleShareClick={handleShareClick}
                    openMatchNoteSettingModal={openMatchNoteSettingModal}
                    disableMakeCopy={disableMakeCopyForConfluent}
                  />
                  {location.state?.tab !== 'emr' && (
                    <Button
                      color="primary"
                      size="sm"
                      onClick={addSection}
                      disabled={disableEditing}
                    >
                      Add Section
                    </Button>
                  )}
                  {!haveInjector &&
                  trainingData &&
                  (Object.keys(trainingData).length > 0 || hasBuilderDocs) ? (
                    <Button
                      color="primary"
                      size="sm"
                      onClick={() =>
                        hasBuilderDocs
                          ? navigate(`/User/templates/builder/pdf`)
                          : navigate(`/User/templates/builder#${configID}`)
                      }
                    >
                      Resume Builder Template
                    </Button>
                  ) : null}
                  {enabled && Object.keys(questions).length > 0 &&
                    !disableMakeCopyForConfluent && (
                      <RelearnStyleButton key={configID} configId={configID} />
                    )}
                  {isDev && (
                    <Button
                      color={isPromoted ? 'success' : 'danger'}
                      size="sm"
                      onClick={() =>
                        isPromoted
                          ? handleViewPromotedTemplate()
                          : openConfirmationModal('promote')
                      }
                    >
                      <i
                        className={
                          isPromoted ? 'fas fa-check' : 'fas fa-arrow-up'
                        }
                        style={{ marginRight: '5px' }}
                      ></i>
                      {isPromoted ? 'Promoted' : 'Promote'}
                    </Button>
                  )}
                  {showInstructionAssistant && (
                    <Button
                      color="primary"
                      size="sm"
                      onClick={() => setInstructionAssistantOpen(true)}
                      title="Instruction Assistant"
                      aria-label="Instruction Assistant"
                    >
                      <i className="fa fa-wand-magic-sparkles"></i>
                    </Button>
                  )}
                  {instructionAssistantUpdate.state.status !== 'idle' && (
                    <div
                      style={{
                        flex: '0 1 320px',
                        maxWidth: '320px',
                        minWidth: '260px',
                      }}
                    >
                      <InstructionUpdateStatusStrip
                        status={instructionAssistantUpdate.state.status}
                        kind={instructionAssistantUpdate.state.kind}
                        onUndo={instructionAssistantUpdate.openUndo}
                        compact
                      />
                    </div>
                  )}
                </div>
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    marginBottom: '20px',
                  }}
                ></div>

                {/* <ul> */}
                <DragDropContext onDragEnd={handleDragEnd}>
                  <Droppable droppableId="droppable-1">
                    {(provided) => (
                      <ul
                        style={{
                          paddingInlineStart: '20px',
                          paddingInlineEnd: '20px',
                        }}
                        {...provided.droppableProps}
                        ref={provided.innerRef}
                      >
                        {Object.keys(questions).map((key, index) => (
                          <Draggable key={key} draggableId={key} index={index}>
                            {(provided) => (
                              <div
                                ref={provided.innerRef}
                                {...provided.draggableProps}
                                {...provided.dragHandleProps}
                              >
                                <Collapsible
                                  label={
                                    <EditableLabel
                                      labelText="Section"
                                      text={key}
                                      onChange={newKey}
                                      className="h3"
                                      disabled={
                                        location.state?.tab === 'emr'
                                          ? true
                                          : disableEditing
                                      }
                                    />
                                  }
                                  content={questions[key]}
                                  setContent={(newAnswer) =>
                                    updateQuestion(key, newAnswer)
                                  }
                                  onAddSample={() =>
                                    onAddSample(
                                      key,
                                      configID,
                                      _setSamples,
                                      apiClient,
                                      () => setAiThinking(true), // onStart: Show banner
                                      () => setAiThinking(false), // onComplete: Hide banner
                                    )
                                  }
                                  onEditSample={(index, newText) =>
                                    onEditSample(
                                      key,
                                      index,
                                      newText,
                                      configID,
                                      _setSamples,
                                      apiClient,
                                      () => setAiThinking(true), // onStart: Show banner
                                      () => setAiThinking(false), // onComplete: Hide banner
                                    )
                                  }
                                  questionKey={key}
                                  samples={samples}
                                  openConfirmationModal={openConfirmationModal}
                                  isTemplate={true}
                                  configID={configID}
                                  sectionText={key}
                                  disabled={disableEditing}
                                  isEmrTemplate={
                                    location.state?.tab === 'emr' ? true : false
                                  }
                                  handleViewOnlyClick={handleViewOnlyClick}
                                  templateReply={templateReply}
                                  setTemplateReply={setTemplateReplyHandler}
                                  showSelector={true}
                                  triggerShow={expandedSections}
                                  subsections={subsections[key] ?? null}
                                />
                              </div>
                            )}
                          </Draggable>
                        ))}
                        {provided.placeholder}
                      </ul>
                    )}
                  </Droppable>
                </DragDropContext>
              </div>
            </Card>
          </div>
        </Row>
      </Container>
      <ConfirmationModal
        isOpen={confirmationDetails.isOpen}
        onClose={() =>
          setConfirmationDetails({ ...confirmationDetails, isOpen: false })
        }
        onConfirm={handleConfirmAction}
        confirmButtonText={
          confirmationDetails.type === 'promote' ? 'Promote' : 'Delete'
        }
      >
        {confirmationDetails.type === 'sample'
          ? 'Are you sure you want to delete this sample?'
          : confirmationDetails.type === 'promote'
            ? 'Are you sure you want to promote this template?'
            : 'Are you sure you want to delete this section?'}
      </ConfirmationModal>

      {/* Promote Success Modal */}
      <ConfirmationModal
        isOpen={promoteSuccessModal.isOpen}
        onClose={handleStayHere}
        onConfirm={handleViewInLibrary}
        confirmButtonText="View in Library"
      >
        Your template has been successfully promoted to the template library!
      </ConfirmationModal>

      <ShareTemplateModal
        isOpen={isShareModalOpen}
        onClose={() => setIsShareModalOpen(false)}
        configID={configID}
        sharedAccessData={sharedAccessData}
      />

      <MatchNoteSettingModal
        isOpen={isMatchNoteSettingModalOpen}
        onClose={closeMatchNoteSettingModal}
        name={name}
        emrUrl={emrUrl}
        availableNoteTypes={availableNoteTypes}
        selectedNoteTypes={selectedNoteTypes}
        onToggleNoteType={toggleSelectedNoteType}
        onSave={saveMatchedNoteTypes}
      />

      <Modal
        isOpen={showViewOnlyModal}
        onClose={() => setShowViewOnlyModal(false)}
        buttons={
          disableMakeCopyForConfluent
            ? [
                {
                  label: 'Close',
                  color: 'secondary',
                  onClick: () => setShowViewOnlyModal(false),
                },
              ]
            : [
                {
                  label: 'Make a Copy',
                  color: 'primary',
                  onClick: async () => {
                    await duplicateTemplate();
                    setShowViewOnlyModal(false);
                  },
                },
                {
                  label: 'Close',
                  color: 'secondary',
                  onClick: () => setShowViewOnlyModal(false),
                },
              ]
        }
      >
        <div style={{ textAlign: 'center', padding: 16 }}>
          <h3>This template is view-only.</h3>
          <p>
            To make changes, create your own copy first.
            <br />
            <br />
            <strong>Would you like to copy this template?</strong>
          </p>
        </div>
      </Modal>
    </>
  );
};

export default TemplateDetails;
